/**
 * Carousel accessibility layer for every Swiper instance in the theme.
 *
 * Loaded right after swiper.min.js so `Swiper.use()` runs before any instance is created.
 * Set `window.CAROUSEL_A11Y_DEBUG = true` to log every announcement.
 * Name a carousel explicitly with `data-carousel-label="…"`, otherwise it takes the nearest
 * heading in its section.
 *
 * Each instance runs in one of two modes, re-picked on every breakpoint:
 *
 *   slideshow  (slidesPerView === 1) — off-screen slides leave the accessibility tree and the
 *              tab order, which is what stops a screen reader reading the previous slide while
 *              the next one is displayed. The arrow keys are then how they are reached.
 *
 *   list       (slidesPerView > 1 or "auto") — a row of items, all of which stay reachable.
 *              Hiding them would only cost keyboard access: they are not "the slide that is not
 *              showing", they are the rest of the row.
 */
(function () {
  'use strict';

  if (typeof Swiper === 'undefined' || typeof Swiper.use !== 'function') return;

  const USER_INTENT_MS = 1000;
  const ANNOUNCE_DELAY_MS = 60;
  const FALLBACK_GRACE_MS = 250;
  const RESTORE_FOCUS_MS = 1000;
  // Status text is wiped afterwards so it is not read again as ordinary page content.
  const CLEAR_DELAY_MS = 2000;
  // A slide change this soon after focus landed inside a slide was caused by that focus.
  const FOCUS_SETTLE_MS = 700;
  const MAX_LABEL_LENGTH = 120;

  const SUPPORTS_INERT = 'inert' in HTMLElement.prototype;
  // Composite widgets own the arrow keys for their own items — a radiogroup of colour swatches
  // moves between swatches with them. Stepping the carousel as well moves focus twice per press
  // and scrolls the row away under whoever is using it.
  const ARROW_KEY_WIDGETS =
    '[role="radiogroup"],[role="tablist"],[role="listbox"],[role="menu"],[role="menubar"],' +
    '[role="tree"],[role="grid"],[role="slider"],[role="spinbutton"]';
  const TEXT = window.theme.strings.carousel;

  Swiper.extendDefaults({
    a11y: {
      containerRole: 'group',
      containerRoleDescriptionMessage: TEXT.carousel,
      itemRoleDescriptionMessage: TEXT.slide,
      slideLabelMessage: TEXT.slidePosition.replace('[index]', '{{index}}').replace('[count]', '{{slidesLength}}'),
    },
  });

  function text(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function truncate(value) {
    return value.length > MAX_LABEL_LENGTH ? value.slice(0, MAX_LABEL_LENGTH).trim() + '…' : value;
  }

  function carouselName(el) {
    const explicit = text(el.getAttribute('data-carousel-label')) || text(el.getAttribute('aria-label'));
    if (explicit) return explicit;

    const section = (el.closest && el.closest('.shopify-section')) || el.parentElement;
    const heading = section && section.querySelector('h1, h2, h3, h4, h5, h6');
    return heading ? truncate(text(heading.textContent)) : TEXT.fallbackName;
  }

  function slideName(slide) {
    if (!slide) return '';

    const image = slide.querySelector('img[alt]');
    const fromImage = image ? text(image.getAttribute('alt')) : '';
    if (fromImage) return truncate(fromImage);

    return truncate(text(slide.textContent));
  }

  let stylesInjected = false;
  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;

    // currentColor is the slide's own text colour, so the ring contrasts with the slide's
    // background whatever palette the section uses.
    const style = document.createElement('style');
    style.textContent =
      '.swiper-slide[tabindex]:focus-visible{outline:2px solid currentColor;outline-offset:-4px}';
    (document.head || document.documentElement).appendChild(style);
  }

  Swiper.use([
    function carouselA11y(params) {
      const swiper = params.swiper;
      const on = params.on;

      let region = null;
      let announceTimer = null;
      let clearTimer = null;
      let lastIntentAt = 0;
      let lastSlideFocusAt = 0;
      let focusHeld = false;
      let hoverHeld = false;
      let hidingSlides = false;
      let restoreFocusUntil = 0;

      function markUserIntent() {
        lastIntentAt = Date.now();
      }

      function hasRecentIntent() {
        return Date.now() - lastIntentAt < USER_INTENT_MS;
      }

      function slideSelector() {
        return '.' + (swiper.params.slideClass || 'swiper-slide');
      }

      function slideOf(node) {
        return node && node.closest ? node.closest(slideSelector()) : null;
      }

      function isSlideshow() {
        return swiper.params.watchSlidesProgress === true && swiper.params.slidesPerView === 1;
      }

      function hasAutoplay() {
        return !!(swiper.params.autoplay && swiper.params.autoplay.enabled);
      }

      // Loop mode reorders the slide nodes in place, so activeIndex is not a stable way back to
      // the node on screen. The active class always is.
      function activeSlideEl() {
        const activeClass = swiper.params.slideActiveClass || 'swiper-slide-active';
        for (let i = 0; i < swiper.slides.length; i++) {
          if (swiper.slides[i].classList.contains(activeClass)) return swiper.slides[i];
        }
        return swiper.slides[swiper.activeIndex] || null;
      }

      function slidePosition(slide) {
        if (swiper.params.loop && slide) {
          const logical = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
          if (!isNaN(logical)) return logical + 1;
        }
        return (swiper.params.loop ? swiper.realIndex : swiper.activeIndex) + 1;
      }

      function slideCount() {
        if (!swiper.params.loop) return swiper.slides.length;

        const seen = {};
        let count = 0;
        swiper.slides.forEach(function (slide) {
          const key = slide.getAttribute('data-swiper-slide-index');
          if (key === null) {
            count += 1;
            return;
          }
          if (!seen[key]) {
            seen[key] = true;
            count += 1;
          }
        });
        return count;
      }

      function setSlideHidden(slide, hidden) {
        if (hidden) {
          slide.setAttribute('aria-hidden', 'true');
          if (SUPPORTS_INERT) slide.inert = true;
        } else {
          slide.removeAttribute('aria-hidden');
          if (SUPPORTS_INERT) slide.inert = false;
        }
      }

      function revealSlides() {
        if (!swiper.slides) return;
        swiper.slides.forEach(function (slide) {
          setSlideHidden(slide, false);
        });
        hidingSlides = false;
      }

      function refreshSlideVisibility() {
        if (!swiper.slides || !swiper.slides.length) return;

        if (!isSlideshow()) {
          if (hidingSlides) revealSlides();
          return;
        }

        const visibleClass = swiper.params.slideVisibleClass || 'swiper-slide-visible';
        const visibleSlides = swiper.visibleSlides || [];
        const active = document.activeElement;

        function isVisible(slide) {
          return (
            slide.classList.contains(visibleClass) ||
            visibleSlides.indexOf(slide) !== -1 ||
            (active && slide.contains(active))
          );
        }

        // Before Swiper has resolved visibility, hiding everything would be worse than nothing.
        if (!swiper.slides.some(isVisible)) return;

        swiper.slides.forEach(function (slide) {
          setSlideHidden(slide, !isVisible(slide));
        });
        hidingSlides = true;
      }

      // Android/TalkBack only mirrors its accessibility cursor into DOM focus on nodes that are
      // focusable, and Swiper's slides are role="group" with no tabindex. With nothing to focus,
      // `focusin` never fires and the autoplay hold below never engages — autoplay runs on under
      // the reader. iOS VoiceOver is unaffected; Safari moves DOM focus on its own.
      //
      // Slideshow only. In list mode scrollOnFocus stays true, so a focus landing on an
      // off-screen item would scroll the row out from under whoever is exploring it.
      function syncSlideFocusTargets() {
        if (!swiper.slides) return;

        const slideshow = isSlideshow();
        swiper.slides.forEach(function (slide) {
          if (slideshow) {
            if (!slide.hasAttribute('tabindex')) {
              slide.setAttribute('tabindex', '-1');
              slide.dataset.carouselA11yTabindex = '';
            }
          } else if ('carouselA11yTabindex' in slide.dataset) {
            slide.removeAttribute('tabindex');
            delete slide.dataset.carouselA11yTabindex;
          }
        });
      }

      // Slideshow mode keeps focus out of the hidden slides, so Swiper's scrollOnFocus has
      // nothing left to do and only ever fired on stray focus. In list mode it is the mechanism
      // that brings a focused off-screen item into view.
      function syncMode() {
        if (swiper.params.a11y) swiper.params.a11y.scrollOnFocus = !isSlideshow();
        syncSlideFocusTargets();
        refreshSlideVisibility();
      }

      // The focus hold is what makes the inert slides safe: without it autoplay advances under
      // someone tabbing a slide's links, that slide goes inert and drops their focus to the body.
      //
      // stop/start rather than pause/resume, because pause() defers to a `waitForTransition`
      // handler that can resume on its own. Swiper's pauseOnMouseEnter is unused for the same
      // reason — its pointerleave resumes unconditionally, even with focus still inside.
      function syncAutoplay() {
        if (!swiper.autoplay || !hasAutoplay()) return;

        const shouldRun = !focusHeld && !hoverHeld;
        if (shouldRun && !swiper.autoplay.running) swiper.autoplay.start();
        else if (!shouldRun && swiper.autoplay.running) swiper.autoplay.stop();
      }

      function focusSlide(slide) {
        if (!slide) return;
        if (!slide.hasAttribute('tabindex')) slide.setAttribute('tabindex', '-1');
        // A focus() call on an inert element is dropped silently, so reveal it first.
        setSlideHidden(slide, false);
        slide.focus({ preventScroll: true });
      }

      // `slideNext()` runs `loopFix()` first, and loopFix moves slide nodes with append() and
      // prepend(), which blurs whatever they contain.
      function restoreSlideFocus() {
        if (Date.now() > restoreFocusUntil) return;

        const active = document.activeElement;
        if (active && active !== document.body && swiper.el.contains(active)) return;

        focusSlide(activeSlideEl());
      }

      function step(delta) {
        restoreFocusUntil = Date.now() + RESTORE_FOCUS_MS;

        if (isSlideshow()) {
          if (delta > 0) swiper.slideNext();
          else swiper.slidePrev();

          // Reveal the new slide while the old one still holds focus, move focus onto it, and
          // only then let the old one go inert.
          refreshSlideVisibility();
          focusSlide(activeSlideEl());
          refreshSlideVisibility();
          return;
        }

        // Loop mode keeps DOM order equal to visual order, so the neighbouring node is the
        // neighbouring item, and focusing it is enough — scrollOnFocus does the scrolling.
        // Calling slideNext() here would instead jump focus to the leftmost visible item.
        const current = slideOf(document.activeElement);
        const index = current ? swiper.slides.indexOf(current) : swiper.activeIndex;
        const target = swiper.slides[index + delta];

        if (target) {
          focusSlide(target);
          return;
        }

        if (delta > 0) swiper.slideNext();
        else swiper.slidePrev();
        focusSlide(activeSlideEl());
      }

      function jump(toEnd) {
        restoreFocusUntil = Date.now() + RESTORE_FOCUS_MS;

        if (swiper.params.loop) swiper.slideToLoop(toEnd ? slideCount() - 1 : 0);
        else swiper.slideTo(toEnd ? swiper.slides.length - 1 : 0);

        refreshSlideVisibility();
        focusSlide(activeSlideEl());
        refreshSlideVisibility();
      }

      function atEdge(delta) {
        if (swiper.params.loop || swiper.params.rewind) return false;
        return delta > 0 ? swiper.isEnd : swiper.isBeginning;
      }

      function ignoresArrowKeys(target) {
        if (!target || !target.tagName) return false;
        if (target.isContentEditable) return true;
        if (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return true;
        return !!(target.closest && target.closest(ARROW_KEY_WIDGETS));
      }

      function onKeydown(event) {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (ignoresArrowKeys(event.target)) return;

        const root = event.target.closest && event.target.closest('.swiper, .swiper-container');
        if (root && root !== swiper.el) return;

        const horizontal = swiper.isHorizontal();
        const back = horizontal ? 'ArrowLeft' : 'ArrowUp';
        const forward = horizontal ? 'ArrowRight' : 'ArrowDown';
        let delta = 0;

        if (event.key === back) delta = -1;
        else if (event.key === forward) delta = 1;
        else if (event.key !== 'Home' && event.key !== 'End') return;

        if (delta && horizontal && swiper.rtlTranslate) delta = -delta;

        event.preventDefault();
        markUserIntent();

        if (event.key === 'Home' || event.key === 'End') {
          jump(event.key === 'End');
        } else if (atEdge(delta)) {
          say(delta > 0 ? TEXT.lastSlide : TEXT.firstSlide);
        } else {
          step(delta);
        }
      }

      function onFocusIn(event) {
        const slide = slideOf(event.target);
        if (slide) {
          if (slide.getAttribute('aria-hidden') === 'true') setSlideHidden(slide, false);
          // In list mode Swiper scrolls this slide into view; that slide change must not speak.
          lastSlideFocusAt = Date.now();
        }

        focusHeld = true;
        syncAutoplay();
      }

      function onFocusOut(event) {
        if (swiper.el && swiper.el.contains(event.relatedTarget)) return;
        focusHeld = false;
        syncAutoplay();
      }

      function onPointerEnter(event) {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        hoverHeld = true;
        syncAutoplay();
      }

      function onPointerLeave(event) {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        hoverHeld = false;
        syncAutoplay();
      }

      // An auto-rotating carousel stays silent until the user drives it; announcing every few
      // seconds would fail 2.2.2.
      function shouldAnnounce() {
        if (Date.now() - lastSlideFocusAt < FOCUS_SETTLE_MS) return false;

        const autoplaying = swiper.autoplay && swiper.autoplay.running && !swiper.autoplay.paused;
        return !autoplaying || hasRecentIntent();
      }

      function say(message) {
        if (!region) return;

        region.textContent = message;

        clearTimeout(clearTimer);
        clearTimer = setTimeout(function () {
          if (region) region.textContent = '';
        }, CLEAR_DELAY_MS);

        if (window.CAROUSEL_A11Y_DEBUG) {
          console.log('[carousel-a11y]', swiper.el.className, '→', message);
        }
      }

      function announce() {
        const slide = activeSlideEl();
        const name = slideName(slide);
        say(TEXT.slideAnnouncement.replace('[index]', slidePosition(slide)).replace('[count]', slideCount()) + (name ? ': ' + name : ''));
      }

      function scheduleAnnounce(delay) {
        clearTimeout(announceTimer);
        announceTimer = setTimeout(announce, delay);
      }

      on('afterInit', function () {
        if (!swiper.params.a11y || !swiper.params.a11y.enabled) return;
        if (!swiper.el || !swiper.el.appendChild) return;

        if (!swiper.el.getAttribute('aria-label')) {
          swiper.el.setAttribute('aria-label', carouselName(swiper.el));
        }

        // Swiper puts aria-live on the wrapper, where it can never fire because the slides only
        // move by transform. Silence it so the region below is the single source of speech.
        if (swiper.wrapperEl) swiper.wrapperEl.setAttribute('aria-live', 'off');

        region = document.createElement('span');
        region.className = 'visually-hidden carousel-a11y__status';
        region.setAttribute('aria-live', 'polite');
        region.setAttribute('aria-atomic', 'true');
        swiper.el.appendChild(region);

        if (swiper.pagination && swiper.pagination.el) {
          const paginationEls = Array.isArray(swiper.pagination.el)
            ? swiper.pagination.el
            : [swiper.pagination.el];
          paginationEls.forEach(function (el) {
            el.addEventListener('click', markUserIntent);
            el.addEventListener('keydown', markUserIntent);
          });
        }

        injectStyles();
        swiper.el.addEventListener('focusin', onFocusIn);
        swiper.el.addEventListener('focusout', onFocusOut);
        swiper.el.addEventListener('keydown', onKeydown);
        if (hasAutoplay()) {
          swiper.el.addEventListener('pointerenter', onPointerEnter);
          swiper.el.addEventListener('pointerleave', onPointerLeave);
        }

        syncMode();
      });

      on('touchStart navigationNext navigationPrev keyPress scrollbarDragStart', markUserIntent);

      // Safety net for an instance whose transition end never arrives. Gated on intent so it can
      // never fire for one of Swiper's internal repositions.
      on('slideChange', function () {
        if (!region || !hasRecentIntent()) return;
        scheduleAnnounce((swiper.params.speed || 0) + FALLBACK_GRACE_MS);
      });

      // Announcing here rather than on slideChange keeps the speech in step with the slide on
      // screen, and Swiper only emits it for real transitions.
      on('slideChangeTransitionEnd', function () {
        restoreSlideFocus();
        refreshSlideVisibility();
        if (!region || !shouldAnnounce()) return;
        scheduleAnnounce(ANNOUNCE_DELAY_MS);
      });

      on('loopFix', restoreSlideFocus);
      on('transitionEnd observerUpdate', refreshSlideVisibility);
      on('breakpoint update resize', syncMode);

      on('destroy', function () {
        clearTimeout(announceTimer);
        clearTimeout(clearTimer);

        if (swiper.el) {
          swiper.el.removeEventListener('focusin', onFocusIn);
          swiper.el.removeEventListener('focusout', onFocusOut);
          swiper.el.removeEventListener('keydown', onKeydown);
          swiper.el.removeEventListener('pointerenter', onPointerEnter);
          swiper.el.removeEventListener('pointerleave', onPointerLeave);
        }

        revealSlides();

        if (region && region.parentNode) region.parentNode.removeChild(region);
        region = null;
      });
    },
  ]);
})();
